import React, { useEffect, useState } from "react";
import { doc, getDoc, setDoc } from "firebase/firestore";
import {
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject,
} from "firebase/storage";
import { db, storage } from "../../firebase";
import "./admin.css";

interface DespreNoiConfig {
  slides: string[];
  slideLinks?: string[];       // link-uri pentru slide-uri principale
  vouchers: string[];
  voucherLinks?: string[];     // link-uri pentru voucher-e
  videoId: string;
  beneficiiTitle: string;
  videoTitle: string;
}

const DEFAULT_CONFIG: DespreNoiConfig = {
  slides: [],
  slideLinks: [],
  vouchers: [],
  voucherLinks: [],
  videoId: "FlFUbG6KaYM",
  beneficiiTitle: "Beneficiile membrilor",
  videoTitle:
    "Ghid pas cu pas pentru înscrierea în aplicația Smart Sanitas",
};

export const AdminDespreNoiPage: React.FC = () => {
  const [form, setForm] = useState<DespreNoiConfig>(DEFAULT_CONFIG);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [uploading, setUploading] = useState(false);

  const docRef = doc(db, "siteContent", "despreNoi");

  // -------- LOAD CONFIG --------
  useEffect(() => {
    const load = async () => {
      try {
        const snap = await getDoc(docRef);
        if (snap.exists()) {
          const data = snap.data() as Partial<DespreNoiConfig>;
          setForm({
            slides: data.slides || DEFAULT_CONFIG.slides,
            slideLinks: data.slideLinks || DEFAULT_CONFIG.slideLinks,
            vouchers: data.vouchers || DEFAULT_CONFIG.vouchers,
            voucherLinks:
              data.voucherLinks || DEFAULT_CONFIG.voucherLinks,
            videoId: data.videoId || DEFAULT_CONFIG.videoId,
            beneficiiTitle:
              data.beneficiiTitle || DEFAULT_CONFIG.beneficiiTitle,
            videoTitle:
              data.videoTitle || DEFAULT_CONFIG.videoTitle,
          });
        } else {
          setForm(DEFAULT_CONFIG);
        }
      } catch (e) {
        console.error("Failed to load DespreNoi config:", e);
      } finally {
        setLoading(false);
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateField = (field: keyof DespreNoiConfig, value: any) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setMessage("");
  };

  // -------- SLIDES UPLOAD --------
  const handleSlideFile = async (index: number, file: File) => {
    try {
      setUploading(true);
      const storageRef = ref(
        storage,
        `despre-noi/slides/${Date.now()}_${file.name}`
      );
      await uploadBytes(storageRef, file);
      const url = await getDownloadURL(storageRef);

      const next = [...form.slides];
      next[index] = url;
      updateField("slides", next);
    } catch (e) {
      console.error(e);
      setMessage("❌ Eroare la upload slide.");
    } finally {
      setUploading(false);
    }
  };

  const addSlide = () => {
    updateField("slides", [...form.slides, ""]);
    const nextLinks = [...(form.slideLinks || [])];
    nextLinks.push("");
    updateField("slideLinks", nextLinks);
  };

  const removeSlide = async (index: number) => {
    try {
      const url = form.slides[index];
      if (
        url &&
        url.startsWith("https://firebasestorage.googleapis.com")
      ) {
        const path = decodeURIComponent(
          url.split("/o/")[1].split("?")[0]
        );
        const fileRef = ref(storage, path);
        await deleteObject(fileRef);
        console.log("✅ Slide șters din Storage:", path);
      }
    } catch (e) {
      console.warn(
        "⚠ Nu s-a putut șterge fișierul din Storage pentru slide:",
        e
      );
    }

    const newSlides = form.slides.filter((_, i) => i !== index);
    const newLinks = (form.slideLinks || []).filter(
      (_l, i) => i !== index
    );

    updateField("slides", newSlides);
    updateField("slideLinks", newLinks);
  };

  // -------- VOUCHERS UPLOAD --------
  const handleVoucherFile = async (index: number, file: File) => {
    try {
      setUploading(true);
      const storageRef = ref(
        storage,
        `despre-noi/vouchers/${Date.now()}_${file.name}`
      );
      await uploadBytes(storageRef, file);
      const url = await getDownloadURL(storageRef);

      const next = [...form.vouchers];
      next[index] = url;
      updateField("vouchers", next);
    } catch (e) {
      console.error(e);
      setMessage("❌ Eroare la upload voucher.");
    } finally {
      setUploading(false);
    }
  };

  const addVoucher = () => {
    updateField("vouchers", [...form.vouchers, ""]);
    const nextLinks = [...(form.voucherLinks || [])];
    nextLinks.push("");
    updateField("voucherLinks", nextLinks);
  };

  const removeVoucher = async (index: number) => {
    try {
      const url = form.vouchers[index];
      if (
        url &&
        url.startsWith("https://firebasestorage.googleapis.com")
      ) {
        const path = decodeURIComponent(
          url.split("/o/")[1].split("?")[0]
        );
        const fileRef = ref(storage, path);
        await deleteObject(fileRef);
        console.log("✅ Voucher șters din Storage:", path);
      }
    } catch (e) {
      console.warn(
        "⚠ Nu s-a putut șterge voucherul din Storage:",
        e
      );
    }

    updateField(
      "vouchers",
      form.vouchers.filter((_, i) => i !== index)
    );

    updateField(
      "voucherLinks",
      (form.voucherLinks || []).filter(
        (_l, i) => i !== index
      )
    );
  };

  // -------- SAVE --------
  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setMessage("");

    try {
      // slides
      const cleanedSlides = form.slides.filter(Boolean);
      const alignedSlideLinks = (form.slideLinks || [])
        .slice(0, cleanedSlides.length)
        .map((l) => l || "");

      // vouchers
      const cleanedVouchers = form.vouchers.filter(Boolean);
      const alignedVoucherLinks = (form.voucherLinks || [])
        .slice(0, cleanedVouchers.length)
        .map((l) => l || "");

      await setDoc(
        docRef,
        {
          slides: cleanedSlides,
          slideLinks: alignedSlideLinks,
          vouchers: cleanedVouchers,
          voucherLinks: alignedVoucherLinks,
          videoId: form.videoId || DEFAULT_CONFIG.videoId,
          beneficiiTitle:
            form.beneficiiTitle || DEFAULT_CONFIG.beneficiiTitle,
          videoTitle:
            form.videoTitle || DEFAULT_CONFIG.videoTitle,
          updatedAt: new Date().toISOString(),
        },
        { merge: true }
      );

      setMessage("✔ Salvat cu succes.");
    } catch (e: any) {
      console.error(e);
      setMessage("❌ Eroare la salvare: " + (e.message || ""));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="admin-dn-wrapper">Se încarcă...</div>;
  }

  return (
    <div className="admin-dn-wrapper">
      <h1>Admin – Despre Noi</h1>
      <form className="admin-dn-form" onSubmit={handleSave}>
        {/* Textele */}
        <section>
          <h2>Texte</h2>
          <label>
            Titlu beneficii
            <input
              type="text"
              value={form.beneficiiTitle}
              onChange={(e) =>
                updateField("beneficiiTitle", e.target.value)
              }
            />
          </label>

          <label>
            Titlu video
            <input
              type="text"
              value={form.videoTitle}
              onChange={(e) =>
                updateField("videoTitle", e.target.value)
              }
            />
          </label>
        </section>

        {/* Slide-uri */}
        <section>
          <h2>Slide-uri principale</h2>
          {form.slides.map((url, i) => (
            <div key={i} className="admin-dn-row">
              <input
                type="text"
                placeholder={`Slide URL ${i + 1}`}
                value={url}
                onChange={(e) => {
                  const next = [...form.slides];
                  next[i] = e.target.value;
                  updateField("slides", next);
                }}
              />

              <input
                type="text"
                placeholder="Link (opțional) când se apasă pe slide"
                value={form.slideLinks?.[i] || ""}
                onChange={(e) => {
                  const next = [...(form.slideLinks || [])];
                  next[i] = e.target.value;
                  updateField("slideLinks", next);
                }}
              />

              <input
                type="file"
                accept="image/*"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) handleSlideFile(i, file);
                }}
              />

              <button
                type="button"
                onClick={() => removeSlide(i)}
              >
                ✕
              </button>
            </div>
          ))}

          <button type="button" onClick={addSlide}>
            ➕ Adaugă slide
          </button>
        </section>

        {/* Voucher-uri */}
        <section>
          <h2>Voucher slide-uri</h2>
          {form.vouchers.map((url, i) => (
            <div key={i} className="admin-dn-row">
              <input
                type="text"
                placeholder={`Voucher URL ${i + 1}`}
                value={url}
                onChange={(e) => {
                  const next = [...form.vouchers];
                  next[i] = e.target.value;
                  updateField("vouchers", next);
                }}
              />

              <input
                type="text"
                placeholder="Link (opțional) când se apasă pe voucher"
                value={form.voucherLinks?.[i] || ""}
                onChange={(e) => {
                  const next = [...(form.voucherLinks || [])];
                  next[i] = e.target.value;
                  updateField("voucherLinks", next);
                }}
              />

              <input
                type="file"
                accept="image/*"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) handleVoucherFile(i, file);
                }}
              />

              <button
                type="button"
                onClick={() => removeVoucher(i)}
              >
                ✕
              </button>
            </div>
          ))}

          <button type="button" onClick={addVoucher}>
            ➕ Adaugă voucher
          </button>
        </section>

        {/* Video */}
        <section>
          <h2>YouTube Video</h2>
          <label>
            Video ID (doar codul, nu linkul complet)
            <input
              type="text"
              value={form.videoId}
              onChange={(e) =>
                updateField("videoId", e.target.value)
              }
              placeholder="Ex: FlFUbG6KaYM"
            />
          </label>
        </section>

        {uploading && <p>Se încarcă imaginea...</p>}
        {message && (
          <p className="admin-dn-message">{message}</p>
        )}

        <button type="submit" disabled={saving}>
          {saving ? "Se salvează..." : "💾 Salvează"}
        </button>
      </form>
    </div>
  );
};